// Opportunities tab (§5): which article to push next. Per portal a table of
// the top-25 ranking URLs by impressions with a recommended action.
// Reads om_gsc_reports via /gsc-reports (MARKETING_VIEWS_SPEC_FRANK.md §5).
import type { MarketingDeps } from "./marketing.js";
import { wireMotorRun } from "./marketing-poll.js";

interface Keyword { q: string; pos: number; impr: number; clicks: number; ctr: number }
interface OppItem {
  impr: number; alias: string; url: string;
  article: { id: number; title: string; words: number; catid: number } | null;
  action: string; keywords: Keyword[];
}
interface OppPortal { label: string; slug: string; pages: number; items: OppItem[] }
interface OppSummary { portals: OppPortal[] }
interface ReportsResponse {
  latest: { id: number; runStamp: string; status: string; summary: OppSummary } | null;
  history: Array<{ id: number; runStamp: string; status: string }>;
}

const ACTIONS: Record<string, { label: string; cls: string }> = {
  push_top3: { label: "🟡 Ranking pushen", cls: "warn" },
  expand_thin: { label: "🔴 Ausbauen", cls: "bad" },
  onpage_nonratgeber: { label: "⚪ On-Page", cls: "ok" },
  merged_or_other: { label: "⚪ Konsolidierung", cls: "ok" },
};

function fmtNum(n: number): string { return n.toLocaleString("de-DE"); }

function renderPortal(host: HTMLElement, p: OppPortal): void {
  const block = document.createElement("div");
  block.className = "om-portal";
  const h = document.createElement("h3");
  h.textContent = `${p.label} · ${p.pages} Seiten · Top ${p.items.length}`;
  block.appendChild(h);

  const table = document.createElement("table");
  table.className = "om-table om-opp-table";
  const head = table.createTHead().insertRow();
  for (const label of ["Σ Impr", "Artikel", "Wörter", "Aktion", "Top-Keywords"]) {
    const th = document.createElement("th");
    th.textContent = label;
    head.appendChild(th);
  }
  const tbody = table.createTBody();
  for (const it of p.items) {
    const tr = tbody.insertRow();
    tr.insertCell().textContent = fmtNum(it.impr);

    const artCell = tr.insertCell();
    const title = it.article ? `#${it.article.id} · ${it.article.title}` : it.alias;
    if (it.url) {
      const a = document.createElement("a");
      a.href = it.url; a.target = "_blank"; a.rel = "noopener";
      a.textContent = title;
      artCell.appendChild(a);
    } else {
      artCell.textContent = title;
    }

    tr.insertCell().textContent = it.article ? fmtNum(it.article.words) : "—";

    const actCell = tr.insertCell();
    const a = ACTIONS[it.action] ?? { label: it.action, cls: "ok" };
    const badge = document.createElement("span");
    badge.className = `om-badge ${a.cls}`;
    badge.textContent = a.label;
    actCell.appendChild(badge);

    const kwCell = tr.insertCell();
    kwCell.className = "om-kw";
    kwCell.textContent = it.keywords
      .slice(0, 3)
      .map((k) => `${k.q} (Pos ${k.pos.toFixed(1)}, ${fmtNum(k.impr)} Impr)`)
      .join(" · ");
  }
  block.appendChild(table);
  host.appendChild(block);
}

export function initOpportunitiesTab(m: HTMLElement, deps: MarketingDeps): void {
  const host = m.querySelector("[data-opp-summary]") as HTMLElement | null;
  const historySel = m.querySelector("[data-opp-history]") as HTMLSelectElement | null;
  const msg = m.querySelector('[data-msg="opportunities"]') as HTMLElement | null;
  if (!host || !historySel) return;

  const load = async (): Promise<void> => {
    const r = await deps.api<ReportsResponse>("/api/marketing/gsc-reports");
    const data = r.data;
    if (!data) return;
    historySel.textContent = "";
    for (const h of data.history) {
      const o = document.createElement("option");
      o.value = String(h.id);
      o.textContent = `${h.runStamp} (${h.status})`;
      historySel.appendChild(o);
    }
    host.textContent = "";
    if (!data.latest) {
      host.innerHTML = '<p class="dim">Noch kein Opportunities-Report. „Jetzt prüfen" startet den Motor.</p>';
      return;
    }
    for (const p of data.latest.summary.portals) renderPortal(host, p);
  };

  m.querySelector('[data-action="open-opp-report"]')?.addEventListener("click", () => {
    const id = historySel.value;
    if (id) window.open(`/api/marketing/gsc-reports/${id}/markdown`, "_blank");
  });

  wireMotorRun({
    deps, modal: m,
    btn: m.querySelector('[data-action="run-opportunities"]'),
    msg, jobType: "gsc_opportunities",
    runUrl: "/api/marketing/motors/gsc_opportunities/run",
    onDone: load,
  });

  void load();
}
